import React, { lazy, Suspense, useEffect } from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spinner, AudioPlayer } from "../Components";
import ScrollToTop from "./ScrollToTop";
import { GetUser } from "../Store/Modules/User/action";

const Home = lazy(() => import("../Modules/Home"));
const Event = lazy(() => import("../Modules/Event"));
const EventView = lazy(() => import("../Modules/Event/Components/EventView"));
const VoteCategoryView = lazy(() =>
  import("../Modules/Event/Components/VoteCategoryView")
);
const TicketDetails = lazy(() =>
  import("../Modules/Event/Components/TicketDetails")
);
const PaymentFail = lazy(() =>
  import("../Modules/Event/Components/PaymentFail")
);
const PaymentPending = lazy(() =>
  import("../Modules/Event/Components/PaymentPending")
);
const News = lazy(() => import("../Modules/News"));
const NewsPage = lazy(() => import("../Modules/News/Components/NewsPage"));
const About = lazy(() => import("../Modules/Company/Components/About"));
const Contact = lazy(() => import("../Modules/Company/Components/Contact"));
const Music = lazy(() => import("../Modules/Music"));
const Movies = lazy(() => import("../Modules/Movies"));
const Videos = lazy(() => import("../Modules/Videos"));
const AllMusic = lazy(() => import("../Modules/Music/Components/AllMusic"));
const SongView = lazy(() => import("../Modules/Music/Components/SongView"));
const MixView = lazy(() => import("../Modules/Music/Components/MixView"));
const Artist = lazy(() => import("../Modules/Music/Components/Artists"));
const AllArtists = lazy(() => import("../Modules/Music/Components/AllArtists"));
const Playlist = lazy(() => import("../Modules/Music/Components/Playlist"));
const NotFound = lazy(() => import("../Modules/Missing"));
const WatchVideo = lazy(() =>
  import("../Modules/Videos/Components/WatchVideo")
);
const WatchMovie = lazy(() =>
  import("../Modules/Movies/Components/WatchMovie")
);
const MovieCategory = lazy(() =>
  import("../Modules/Movies/Components/MovieCategory")
);
const MusicCategory = lazy(() =>
  import("../Modules/Music/Components/MusicCategory")
);
const VideoCategory = lazy(() =>
  import("../Modules/Videos/Components/VideoCategory")
);
const NomineePage = lazy(() =>
  import("../Modules/Event/Components/NomineePage")
);
const Register = lazy(() => import("../Modules/Auth/Components/Register"));
const Profile = lazy(() => import("../Modules/Profile/Components/Profile"));

// Redirect to /register when no session at all. If we have a token but the
// user object hasn't been hydrated yet, keep rendering the child — Profile
// dispatches GetUser on mount and shows its own loading state.
function ProtectedRoute({ children, ...rest }) {
  const user = useSelector((state) => state.user.user);
  const hasUser = !!(user && user.id);
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("afrika-token");

  return (
    <Route
      {...rest}
      render={() => {
        if (hasUser || hasToken) return children;
        return <Redirect to="/register" />;
      }}
    />
  );
}

export default function Routes() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);

  // Hydrate the user on app boot if a token is present in localStorage.
  // The existing GetUser action swallows 401s (it checks a non-existent `error`
  // key on the envelope), which would strand a user on /profile with an
  // expired token and no way out. Guard that here: fetch /user directly, and
  // if the server says the token is no good, drop it. GetUser still runs to
  // populate Redux on the happy path.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = localStorage.getItem("afrika-token");
    if (!token || (user && user.id)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://api.africaplusworld.com/v1/user", {
          headers: { Accept: "application/json", Authorization: "Bearer " + token },
        });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem("afrika-token");
          return;
        }
        // Delegate to the shared action so the Redux user slice hydrates the
        // same way it does after Register — one code path for state shape.
        dispatch(GetUser(token));
      } catch {
        // Network error — don't wipe the token; the user may just be offline.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
      <AudioPlayer>
        <Suspense fallback={<Spinner />}>
          <ScrollToTop />
          <Switch>
            <Route path="/" exact component={Home} />
            <Route path="/about-us" exact component={About} />
            <Route path="/contact-us" exact component={Contact} />
            <Route path="/events" exact component={Event} />
            <Route path="/event/:id" exact component={EventView} />
            <Route path="/payment-fail" exact component={PaymentFail} />
            <Route path="/payment-pending" exact component={PaymentPending} />
            <Route path="/nominee/:id" exact component={NomineePage} />
            <Route path="/vote-category/:id" exact component={VoteCategoryView} />
            <Route path="/ticket/:id" exact component={TicketDetails} />
            <Route path="/music" exact component={Music} />
            <Route path="/movies" exact component={Movies} />
            <Route path="/video" exact component={Videos} />
            <Route path="/artists" exact component={AllArtists} />
            <Route path="/artist/:id" exact component={Artist} />
            <Route path="/video/:id" exact component={WatchVideo} />
            <Route path="/movie/:id" exact component={WatchMovie} />
            <Route path="/movie-category/:id" exact component={MovieCategory} />
            <Route path="/video-category/:id" exact component={VideoCategory} />
            <Route path="/music-category/:id" exact component={MusicCategory} />
            <Route path="/all-music" exact component={AllMusic} />
            <Route path="/music/:id" exact component={SongView} />
            <Route path="/mix/:id" exact component={MixView} />
            <Route path="/album/:id" exact component={Playlist} />
            <Route path="/inside-africa" exact component={News} />
            <Route path="/inside-africa/:id" exact component={NewsPage} />
            <Route path="/register" exact component={Register} />
            <ProtectedRoute path="/profile" exact>
              <Profile />
            </ProtectedRoute>
            <Route path="/404" component={NotFound} />
            <Redirect to="/404" />
          </Switch>
        </Suspense>
      </AudioPlayer>
  );
}
